// @flow

import { all, call, put, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";

import http from "@http";
import actionCreator from "@modules/shared/actionCreator";
import * as actions from "@modules/shared/constants/actions";
import type { SelectedTask } from "@modules/reporting/reducer/tasks/types";
import config from "@config";

type CreateAbsenceReviewTaskRequest = $Exact<{
  clientId: number,
  employeeId: number,
}>;

export function* onCreateAbsenceReviewTask({
  payload,
}: Action<CreateAbsenceReviewTaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/tools/client/${payload.clientId}/tools/task/absence-monitoring-review/employee/${payload.employeeId}/submit`,
    );

    yield all([put(actionCreator.CreateAbsenceReviewTaskSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreator.CreateAbsenceReviewTaskError(e.response));
  }
}

type GetAbsenceReviewTaskRequest = $Exact<{
  clientId: number,
  taskId: number,
  referralType: "employee" | "manager",
}>;

export function* onGetLastAbsenceReviewTask({
  payload,
}: Action<GetAbsenceReviewTaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/tools/client/${payload.clientId}/tools/task/absence-monitoring-review/${payload.taskId}`,
    );

    yield all([put(actionCreator.GetLastAbsenceReviewTaskSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreator.GetLastAbsenceReviewTaskError(e.response));
  }
}

export function* onGetAbsenceReviewTask({
  payload,
}: Action<GetAbsenceReviewTaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/tools/client/${payload.clientId}/tools/task/absence-monitoring-review/${payload.taskId}`,
    );

    yield all([put(actionCreator.GetAbsenceReviewTaskSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreator.GetAbsenceReviewTaskError(e.response));
  }
}
export function* onGetReferralDecisionTaskData({
  payload,
}: Action<GetAbsenceReviewTaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId || !payload.referralType) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/task/${payload.referralType}-referral/${payload.taskId}`,
    );

    yield put(actionCreator.GetReferralDecisionTaskDataSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetReferralDecisionTaskDataError(e.response));
  }
}

type GetManualReferralTaskRequest = $Exact<{
  clientId: number,
  taskId: number,
  employeeId: number,
}>;
export function* onGetManualReferralTaskData({
  payload,
}: Action<GetManualReferralTaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const { clientId, taskId, employeeId } = payload;

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${clientId}/employee/${employeeId}/manual-referral/${taskId}`,
    );

    yield all([
      put(actionCreator.GetManualReferralTaskDataSuccess(response.data)),
      put(actionCreator.ToggleManualReferralTaskDataModal()),
    ]);
  } catch (e) {
    yield put(actionCreator.GetManualReferralTaskDataError(e.response));
  }
}

type CancelAbsenceReviewTaskRequest = $Exact<{
  clientId: number,
  taskId: number,
  employeeId: number,
}>;

export function* onCancelAbsenceReviewTask({
  payload,
}: Action<CancelAbsenceReviewTaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/tools/client/${payload.clientId}/tools/task/absence-monitoring-review/${payload.taskId}/cancel`,
    );

    yield all([
      put(actionCreator.CancelAbsenceReviewTaskSuccess(response.data)),
      put(
        actionCreator.GetEmployeeEventHistory({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreator.CancelAbsenceReviewTaskError(e.response));
  }
}

type SubmitMonitoringUpdateRequest = $Exact<{
  clientId: number,
  employeeId: number,
  taskId: number,
  task: SelectedTask,
}>;

export function* onSubmitAbsenceReviewTask({
  payload,
}: Action<SubmitMonitoringUpdateRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.taskId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.post,
      `/api/v1/tools/client/${payload.clientId}/tools/task/absence-monitoring-review/${payload.taskId}/submit`,
      payload.task,
    );

    yield all([
      put(actionCreator.SubmitAbsenceReviewTaskSuccess()),
      put(
        actionCreator.GetEmployeeEventHistory({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreator.SubmitAbsenceReviewTaskError(e.response));
  }
}

export function* TasksSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(actions.GetLastAbsenceReviewTask, onGetLastAbsenceReviewTask),
    takeEvery(actions.GetAbsenceReviewTask, onGetAbsenceReviewTask),
    takeEvery(actions.GetReferralDecisionTaskData, onGetReferralDecisionTaskData),
    takeEvery(actions.GetManualReferralTaskData, onGetManualReferralTaskData),
    takeEvery(actions.CreateAbsenceReviewTask, onCreateAbsenceReviewTask),
    takeEvery(actions.CancelAbsenceReviewTask, onCancelAbsenceReviewTask),
    takeEvery(actions.SubmitAbsenceReviewTask, onSubmitAbsenceReviewTask),
  ]);
}

export default TasksSaga;
